import React, { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Spin,
} from "antd";

import { ArrowRightOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import SingleTitleWithSearch from "components/Common/SearchableHiringCompany/SearchableHiringCompany";
import SearchableJobTitle from "components/Common/SearchableHiringCompany/SearchableJobTitle";
import MultiSkillSelector from "components/Select/MultiSkillSelector";
import LocationSearchInput from "../LocationSearchInput";
import { createProject } from "api/project.api";
import MultiSearchableJobTitle from "components/Common/SearchableHiringCompany/MultiSearchableJobTitle";
import Label from "components/Common/Label/Label";
import GoogleLocationWithDropdown from "components/Common/SearchableDropdown/GoogleLocationWithDropdown";
import UploadFile from "components/Common/Input/UploadFileNew";
import CustomLabel from "components/Common/Label/CustomLabel";
import { UploadProps } from "antd/lib";
import MultiKeywordSelector from "components/Select/MultiKeywordSelector";
import AddCandidateCriteria from "components/HRFlow/steps/Step4Candidates/AddCandidateCriteria";

const { TextArea } = Input;
const { Option } = Select;

interface ThirdStepOnboardingFormProps {
  formData: FormData | null; // FormData from Step 2
  onClose: any;
}

const ThirdStepOnboardingForm: React.FC<ThirdStepOnboardingFormProps> = ({ formData, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [hiringCompanies, setHiringCompanies] = useState<any[]>([]);
  // const [jobTitles, setJobTitles] = useState<any[]>([]);
  const [jobTitles, setJobTitles] = useState<any>(null);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [talentPoolCheckbox, setTalentPoolCheckbox] = useState<string>("false");
  const [employmentType, setEmploymentType] = useState<string>("FULL-TIME");
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [fileData, setFileData] = useState<File | null>(null);
  const [searchTitle, setSearchTitle] = useState<any>(null);
  const [positionData, setPositionData] = useState<string | null>(null);
  const [mustHavekeywords, setMustHavekeywords] = useState<boolean>(false);
  const [searchQuestionSetId, setSearchQuestionSetId] = useState<null | number>(null);
  // const initialValues = formData
  //     ? Array.from(formData.entries()).reduce((acc, [key, value]) => {
  //           acc[key] = value;
  //           return acc;
  //       }, {} as Record<string, any>)
  //     : {};
  const initialValues = formData
    ? Array.from(formData.entries()).reduce((acc, [key, value]) => {
      if ((key === "minimum_salary" || key === "maximum_salary") && value === "0") {
        acc[key] = "";
        return acc;
      } else {
        acc[key] = value;
        return acc;
      }
    }, {} as Record<string, any>)
    : {};

  const handleTalentPoolCheckboxChanged = (e: any) => {
    form.setFieldValue("is_talent_pool", JSON.stringify(e.target.checked));
    setTalentPoolCheckbox(JSON.stringify(e.target.checked));
  };

  const clearAllCompanies = () => {
    setHiringCompanies([]);
    form.setFieldsValue({ recruiting_company: [] }); // Clear locations in the form
  };

  const removeComapnies = (companyName: any) => {
    setHiringCompanies((prev) => prev.filter((comp) => comp !== companyName));
    form.setFieldValue(
      "recruiting_company",
      hiringCompanies.filter((comp) => comp !== companyName)
    );
  };

  const handleCityChange = (selected: string[] | string) => {
    const lastSelectedValue = Array.isArray(selected) ? selected[selected.length - 1] : selected;
    setSelectedLocation(lastSelectedValue);
    form.setFieldsValue({ location: lastSelectedValue });
  };

  const handleSearchTitle = (search_title: string) => {
    setSearchTitle(search_title);
    form.setFieldsValue({ search_title: search_title });
    form.setFieldsValue({ title: search_title });
  };

  const handleMustHaveChange = () => {
    setMustHavekeywords(!mustHavekeywords); // Dispatch action to update Redux store
  };
  // const removeJobTitles = (jobTitleName: any) => {
  //     setJobTitles((prev) => prev.filter((comp) => comp !== jobTitleName));
  //     form.setFieldsValue({ official_title: jobTitles.filter((job) => job !== jobTitleName) });
  // };

  // const clearAllJobTitles = () => {
  //     setJobTitles([]);
  //     form.setFieldsValue({ official_title: [] }); // Clear locations in the form
  // };

  const removeLocation = (locationName: any) => {
    setLocations((prev) => prev.filter((loc) => loc !== locationName));
    form.setFieldValue(
      "locations",
      locations.filter((loc) => loc !== locationName)
    );
  };

  const clearAllLocations = () => {
    setLocations([]);
    form.setFieldValue("locations", []); // Clear locations in the form
  };

  const handleCancel = () => {
    onClose();
  };

  const handleProjectCreate = async (formValues: any) => {
    const formData = new FormData();
    formData.append("title", searchTitle);
    if (searchQuestionSetId) {
      formData.append("selected_question_set", searchQuestionSetId.toString());
    }
    formData.append("recruiting_company", JSON.stringify(hiringCompanies));
    // formData.append("locations", JSON.stringify(selectedLocation));
    if (formValues.location) {
      formData.append("locations", JSON.stringify(formValues.location));
    }
    formData.append("official_title", JSON.stringify(jobTitles));
    formData.append("radius", formValues.radius ? formValues.radius : "");
    formData.append("minimum_salary", formValues.minimum_salary?.toString());
    formData.append("maximum_salary", formValues.maximum_salary?.toString());
    formData.append("job_type", employmentType);
    formData.append("description", formValues.description ? formValues.description : "");
    formData.append("keywords", JSON.stringify(keywords)); // Skills can be stringified as JSON
    if (mustHavekeywords) {
      formData.append("must_have_keywords", JSON.stringify(keywords));
    }
    formData.append("currency", formValues.currency || "USD");
    formData.append("is_talent_pool", talentPoolCheckbox);
    // if (formValues.file) {
    //     formData.append("job_description", formValues.file);
    // }
    if (fileData) {
      formData.append("job_description", fileData);
    }
    try {
      const response = await createProject(formData);
      setSearchQuestionSetId(null);
    } catch (error) {
      message.error("Failed to create project!");
      return;
    }
  };

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await handleProjectCreate(values);
      message.success("Onboarding Process Completed successfully!");
      onClose();
    } catch (errorInfo) {
      console.error("Validation Failed:", errorInfo);
    } finally {
      setLoading(false);
    }
  };

  // Set initial values in the form
  React.useEffect(() => {
    if (initialValues?.search_title) {
      setSearchTitle(initialValues.search_title);
    }
    let parsedJobTitles = [];

    try {
      parsedJobTitles = JSON.parse(initialValues.job_title || '[]');
      if (!Array.isArray(parsedJobTitles)) {
        parsedJobTitles = [];
      }
    } catch (e) {
      parsedJobTitles = [];
    }

    const selectedTitle = parsedJobTitles[0] ?? [];

    console.log("177 Position: ", JSON.parse(initialValues.job_title)[0], typeof (JSON.parse(initialValues.job_title)));
    form.setFieldsValue(initialValues);
    form.setFieldsValue({ title: initialValues.search_title });
    form.setFieldsValue({ locations: JSON.stringify(initialValues.locations) });
    setLocations(JSON.parse(initialValues.locations));
    form.setFieldsValue({ keywords: JSON.parse(initialValues.keywords) });
    setKeywords(JSON.parse(initialValues.keywords));
    // form.setFieldsValue({ official_title: initialValues.job_title });
    // setJobTitles(JSON.parse(initialValues.job_title));
    form.setFieldsValue({ official_title: selectedTitle });
    setJobTitles(selectedTitle);
    form.setFieldsValue({ recruiting_company: initialValues.recruiting_company });
    setHiringCompanies(JSON.parse(initialValues.recruiting_company));
  }, [form]);

  const uploadProps: UploadProps = {
    name: "file",
    multiple: true,
    action: "https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload",
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  return (
    <>
      <Card style={{ border: "0" }}>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          {/* <Divider /> */}
          <div style={{ display: "flex", gap: "2rem" }}>
            {/* Left Column */}
            <div style={{ flex: 1 }}>
              <Form.Item
                name="title"
                rules={[{ required: true, message: "Project Name is required!" }]}
              >
                <CustomLabel
                  labelTitle="Project Name"
                  isHelpIconRequired={true}
                  isRequired={true}
                  tooltipText="Enter the project name"
                />
                <Input placeholder="Enter Name" value={searchTitle} onChange={(e) => handleSearchTitle(e.target.value)} />
              </Form.Item>

              <Form.Item
                // label="Hiring Company"
                name="recruiting_company"
                // tooltip="Select the hiring company"
                rules={[{ required: true, message: "Hiring Company is required!" }]}
                initialValue={initialValues.recruiting_company}
                required
              >
                <CustomLabel
                  labelTitle="Hiring Company"
                  isHelpIconRequired={true}
                  isRequired={true}
                  tooltipText="Select the hiring company"
                />
                <SingleTitleWithSearch
                  placeholder="Start Typing"
                  onChange={(value: string) => {
                    const updatedhiringCompanies = [...hiringCompanies, value];
                    form.setFieldsValue({ recruiting_company: updatedhiringCompanies });
                    setHiringCompanies(updatedhiringCompanies);
                  }}
                />
                <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {hiringCompanies.map((comapny) => (
                    <Button key={comapny} size="small" onClick={() => removeComapnies(comapny)}>
                      {comapny} <CloseOutlined />
                    </Button>
                  ))}
                  {hiringCompanies.length > 0 && (
                    <Button
                      onClick={clearAllCompanies}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "auto",
                        padding: "4px 8px",
                        height: "auto",
                        lineHeight: "1",
                      }}
                    >
                      <span>Clear All</span>
                      <CloseOutlined />
                    </Button>
                  )}
                </div>
                {/* </Space> */}
              </Form.Item>

              <Form.Item
                name="official_title"
                tooltip="Enter job titles"
                rules={[{ required: true, message: "Job title is required!" }]}
                initialValue={jobTitles}
              >
                {/* <SearchableJobTitle
                                    placeholder="Position"
                                    onChange={(value: string) => {
                                        const updatedJobTitles = [...jobTitles, value];
                                        form.setFieldsValue({ official_title: updatedJobTitles });
                                        setJobTitles(updatedJobTitles);
                                    }}
                                ></SearchableJobTitle> */}
                <Label labelTitle="Job Title" isHelpIconRequired={false} isRequired={true} />
                <MultiSearchableJobTitle
                  placeholder="Position"
                  defaultValue={jobTitles}
                  setPositionData={setJobTitles}
                  positionData={jobTitles}
                  onChange={(value: any) => {
                    console.log("Value 300: ", value);
                    if (value !== undefined && value !== null) {
                      if (Array.isArray(value)) {
                        setJobTitles(value.length > 0 ? value[value.length - 1].value : null);
                        form.setFieldsValue({ official_title: value.length > 0 ? value[value.length - 1].value : null });
                      } else {
                        setJobTitles(value.value);
                        form.setFieldsValue({ official_title: value.value });
                      }
                      // setJobTitles(value.value);
                    } else {
                      form.setFieldsValue({ official_title: null });
                      setJobTitles(null);
                    }

                  }}
                ></MultiSearchableJobTitle>
                {/* <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                    {jobTitles.map((jobTitle) => (
                                        <Button
                                            key={jobTitle}
                                            size="small"
                                            onClick={() => removeJobTitles(jobTitle)}
                                        >
                                            {jobTitle} <CloseOutlined />
                                        </Button>
                                    ))}
                                    {jobTitles.length > 0 && (
                                        <Button
                                            onClick={() => clearAllJobTitles()}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: "auto",
                                                padding: "4px 8px",
                                                height: "auto",
                                                lineHeight: "1",
                                            }}
                                        >
                                            <span>Clear All</span>
                                            <CloseOutlined />
                                        </Button>
                                    )}
                                </div> */}
              </Form.Item>

              {/* Experiences Field*/}
              <Form.Item
                label="Minimum Experiences"
                name="min_experience"
                tooltip="Select experience"
                rules={[
                  { required: false, message: "Minimum experience is required!" },
                  { pattern: /^\d+$/, message: "Only numeric values are allowed!" },
                ]}
              >
                <Input placeholder="Min Experience..." type="number" />
              </Form.Item>

              {/* Experiences Field */}
              <Form.Item
                label="Maximum Experiences"
                name="max_experience"
                tooltip="Select experience"
                rules={[
                  { required: false, message: "Minimum experience is required!" },
                  { pattern: /^\d+$/, message: "Only numeric values are allowed!" },
                ]}
              >
                <Input placeholder="Max Experience..." type="number" />
              </Form.Item>

              {/* Locations Field with Radius */}
              <div className="flex mb-5">
                {" "}
                <Form.Item
                  name="location"
                  label="Location"
                  // rules={[{ required: true, message: "Please select a location" }]}
                  className="w-3/4 m-0 mr-4"
                  tooltip="Enter Location to select"
                >
                  <Space direction="vertical" size={"small"} className="w-full gap-[6px]">
                    {/* <Label labelTitle="Location" isHelpIconRequired={false} isRequired={false}/> */}
                    <GoogleLocationWithDropdown
                      handleChange={handleCityChange}
                      selectedLocations={selectedLocation}
                      placeholder={"Search for a location..."}
                      isBorder={false}
                    />
                  </Space>
                </Form.Item>
                <Form.Item name="radius" className="w-1/4 m-0">
                  <Space direction="vertical" size={"small"} className="w-full gap-[6px]">
                    <Label labelTitle="Radius (ml)" isHelpIconRequired={false} isRequired={false} />
                    <Input
                      type="number"
                      placeholder="0"
                      rootClassName="py-2 px-3"
                      className="rounded-lg"
                    />
                  </Space>
                </Form.Item>
              </div>
              <Form.Item
                label="Salary Range"
                tooltip="Enter the salary range"
                rules={[{ required: false, message: "Salary range is required!" }]}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "2px",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {/* Minimum Salary Input */}
                  <Form.Item
                    name="minimum_salary"
                    rules={[{ required: false, message: "Minimum salary is required!" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Min Salary" />
                  </Form.Item>

                  {/* Maximum Salary Input */}
                  <Form.Item
                    name="maximum_salary"
                    rules={[{ required: false, message: "Maximum salary is required!" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Max Salary" />
                  </Form.Item>

                  {/* Currency Select */}
                  <Form.Item
                    name="currency"
                    rules={[{ required: false, message: "Currency is required!" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select>
                      <Select.Option value="USD">$</Select.Option>
                      <Select.Option value="EUR">€</Select.Option>
                      <Select.Option value="INR">₹</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </Form.Item>

              {/* Employment Field */}
              <Form.Item label="Employment" name="employment_type" style={{ marginTop: "1rem" }}>
                <Select defaultValue="Full time" onChange={(value) => setEmploymentType(value)}>
                  <Option value="FULL-TIME">Full time</Option>
                  <Option value="PART-TIME">Part time</Option>
                </Select>
              </Form.Item>

            </div>

            {/* Right Column */}
            <div style={{ flex: 1 }}>
              <Form.Item
                label="Keywords"
                name="keywords"
                tooltip="Add required keywords"
                rules={[{ required: false, message: "Keywords are required!" }]}
                initialValue={keywords}
              >
                <MultiKeywordSelector
                  placeholder="eg: React.js"
                  defaultValue={keywords}
                  rootClassName={"border-primary"}
                  // handleDeselect={handleDeselect}
                  handleDeselect={(deselectedSkill: any) => {
                    if (typeof deselectedSkill === "string") {
                      setKeywords(keywords.filter((skill: string) => skill !== deselectedSkill));
                    } else {
                      setKeywords(
                        keywords.filter((skill: string) => skill !== deselectedSkill?.value)
                      );
                    }
                  }}
                  // onChange={onChange}
                  onChange={(selectedSkills: any) => {
                    const skillLabels = selectedSkills.map((skill: any) => skill?.value);
                    console.log("Skill labels: ", skillLabels);
                    setKeywords(skillLabels);
                  }}
                />
                {/* <MultiSkillSelector
                  placeholder="eg: React.js"
                  skills={keywords} // Ensure `skills` is an array
                  rootClassName={
                    (form.getFieldValue("keywords")?.length || 0) > 0 ? "border-primary" : ""
                  }
                  handleDeselect={(deselectedSkill: any) => {
                    if (typeof deselectedSkill === "string") {
                      setKeywords(keywords.filter((skill: string) => skill !== deselectedSkill));
                    } else {
                      setKeywords(
                        keywords.filter((skill: string) => skill !== deselectedSkill?.value)
                      );
                    }
                  }}
                  onChange={(selectedSkills: any) => {
                    const skillLabels = selectedSkills.map((skill: any) => skill?.value);
                    console.log("Skill labels: ", skillLabels);
                    setKeywords(skillLabels);
                  }}
                /> */}
              </Form.Item>
              {/* <div className="checkbox-wrapper flex flex-wrap justify-center items-center gap-2"> */}
              <p className="ant-upload-text" style={{ marginTop: "-5%", color: "darkgray" }}>
                <Checkbox name="must_have_keywords" onChange={handleMustHaveChange} checked={mustHavekeywords}>Must Have</Checkbox>
                Add up to 12 keywords
              </p>
              {/* </div> */}
              <Form.Item
                label="Description"
                name="description"
                tooltip="Enter a description"
                rules={[{ required: false, message: "Description is required!" }]}
                className="mt-4"
              >
                <TextArea rows={10} placeholder="Enter a description..." />
              </Form.Item>
              <Form.Item
                name="file"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  console.log("Event:", e); // Debugging
                  return e?.fileList || [];
                }} // Store fileList
                className="mb-5"
              >
                <Space direction="vertical" size={"small"} className="w-full gap-[6px]">
                  <CustomLabel
                    labelTitle="Upload reference materials"
                    isHelpIconRequired={true}
                    isRequired={false}
                    tooltipText={
                      "This is where you can upload documents you can refer to and use later.\nDocument examples include job descriptions, research documents, interview questions, etc."
                    }
                  />
                  <UploadFile
                    {...uploadProps}
                    secText="PDF and Docx files supported"
                    setFileData={setFileData}
                  />
                </Space>
              </Form.Item>
            </div>

          </div>
          <div className="grid grid-cols-1 gap-4 border p-4 mt-4">
            <AddCandidateCriteria
              search_question_set_id={searchQuestionSetId}
              setSearchQuestionSetId={setSearchQuestionSetId}
            />
          </div>
          <Divider />
          {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "20px", marginBottom: "-7%" }}>
                        <Checkbox name="is_talent_pool" onChange={handleTalentPoolCheckboxChanged}>Create Talent Pool of this Project</Checkbox>
                    </div> */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}
          >
            <Button key="cancel" onClick={handleCancel}>
              <CloseOutlined /> Cancel
            </Button>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                // type="primary" 
                htmlType="submit"
                className="text-white py-1 px-3 rounded-md bg-[#845dda] border border-[#7F56D9]"
              >
                {loading ? (
                  <Spin spinning={loading} size="small" className="custom-spin" />
                ) : (
                  <SaveOutlined />
                )}
                Finish <ArrowRightOutlined />
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default ThirdStepOnboardingForm;
